import React, { useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { apifetch } from '../services/interceptors';
import AuthShell, { AuthField } from '../v2/auth/AuthShell';
const ResetPassword = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: Verification, 3: Reset
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await apifetch(`s1/api/send-verification-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to send verification code.");
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await apifetch(`s1/api/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: verificationCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Invalid verification code.");
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      setLoading(false);
      return;
    }
    try {
      const res = await apifetch(`s1/api/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to reset password.");
      setSuccess(true);
      navigate('/login')
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const mismatch = confirmPassword !== "" && password !== confirmPassword;

  const COPY = {
    1: { title: "Reset your password", sub: "Tell us the address you sign in with and we'll send a code." },
    2: { title: "Verify your email", sub: `Enter the six-digit code we sent to ${email}.` },
    3: { title: "Choose a new password", sub: "Use at least 6 characters. You'll sign in with this from now on." },
  }[step];

  return (
    <AuthShell modules={7}>
      <span className="auth-eyebrow">Step {step} of 3</span>
      <h1 className="auth-title">{COPY.title}</h1>
      <p className="auth-sub">{COPY.sub}</p>

      <div className="auth-progress"><span style={{ width: `${(step / 3) * 100}%` }} /></div>

      {step === 1 && (
        <form onSubmit={handleEmailSubmit} className="auth-fields">
          <AuthField label="Email" required>
            <input className="auth-input" type="email" autoComplete="username"
                   placeholder="you@company.com" value={email}
                   onChange={(e) => { setError(""); setEmail(e.target.value); }} required />
          </AuthField>
          {error && <div className="auth-note">{error}</div>}
          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? "Sending…" : "Send the code"}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleCodeSubmit} className="auth-fields">
          <AuthField label="Verification code" required>
            <input className="auth-input" type="text" inputMode="numeric"
                   autoComplete="one-time-code" placeholder="000000" value={verificationCode}
                   onChange={(e) => { setError(""); setVerificationCode(e.target.value); }} required />
          </AuthField>
          {error && <div className="auth-note">{error}</div>}
          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? "Checking…" : "Verify"}
          </button>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={handlePasswordSubmit} className="auth-fields">
          <AuthField label="New password" required hint="At least 6 characters">
            <input className="auth-input" type="password" autoComplete="new-password" minLength={6}
                   value={password}
                   onChange={(e) => { setError(""); setPassword(e.target.value); }} required />
          </AuthField>

          <AuthField label="Confirm password" required
                     error={mismatch ? "These passwords don't match." : ""}>
            <input className={`auth-input${mismatch ? " bad" : ""}`} type="password"
                   autoComplete="new-password" value={confirmPassword}
                   onChange={(e) => { setError(""); setConfirmPassword(e.target.value); }} required />
          </AuthField>

          {error && <div className="auth-note">{error}</div>}
          {success && <div className="auth-note auth-note--ok">Password reset. Taking you to sign in…</div>}

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? "Saving…" : "Reset password"}
          </button>
        </form>
      )}

      <p className="auth-foot" style={{ marginTop: "1.5rem" }}>
        Remembered it? <Link to="/login">Back to sign in</Link>
      </p>
    </AuthShell>
  );
};

export default ResetPassword;
